import React, { useMemo } from 'react';
import {
  Grid, Card, CardContent, Typography, Stack, Avatar, Chip,
  Box, Divider, List, ListItem, ListItemText, ListItemIcon,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SchoolIcon from '@mui/icons-material/School';
import BookIcon from '@mui/icons-material/Book';
import RoomIcon from '@mui/icons-material/Room';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';

const EducationalScheduleTeachersView = ({ schedules, staffList, dateFilter, selectedDate }) => {
  // Get day name in Arabic
  const getDayName = (dayValue) => {
    const days = {
      'sunday': 'الأحد',
      'monday': 'الاثنين',
      'tuesday': 'الثلاثاء',
      'wednesday': 'الأربعاء',
      'thursday': 'الخميس',
      'friday': 'الجمعة',
      'saturday': 'السبت'
    };
    return days[dayValue] || dayValue;
  };

  // Get current day based on date filter
  const getCurrentDay = () => {
    const today = new Date();
    const dayOfWeekMap = {
      0: 'sunday',
      1: 'monday',
      2: 'tuesday',
      3: 'wednesday',
      4: 'thursday',
      5: 'friday',
      6: 'saturday'
    };

    switch (dateFilter) {
      case 'yesterday':
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        return dayOfWeekMap[yesterday.getDay()];
      case 'today':
      case 'daily':
        return dayOfWeekMap[today.getDay()];
      case 'tomorrow':
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        return dayOfWeekMap[tomorrow.getDay()];
      default:
        return null; // For weekly/monthly, show all days
    }
  };

  // Group schedules by teacher
  const teacherSchedules = useMemo(() => {
    const currentDay = getCurrentDay();
    let filteredSchedules = schedules;

    // Filter by day if specific day is selected
    if (currentDay) {
      filteredSchedules = filteredSchedules.filter(s => s.day_of_week === currentDay);
    }

    // Group by teacher
    const grouped = {};
    filteredSchedules.forEach(schedule => {
      if (!schedule.teacher || !schedule.teacher_name) return;

      const teacherId = schedule.teacher;
      if (!grouped[teacherId]) {
        grouped[teacherId] = {
          teacherId,
          teacherName: schedule.teacher_name,
          classes: new Map(), // Use Map to avoid duplicates
          schedules: []
        };
      }

      // Add class if not already added
      if (schedule.school_class_name) {
        if (!grouped[teacherId].classes.has(schedule.school_class)) {
          grouped[teacherId].classes.set(schedule.school_class, {
            classId: schedule.school_class,
            className: schedule.school_class_name,
            schedules: []
          });
        }
        grouped[teacherId].classes.get(schedule.school_class).schedules.push(schedule);
      }

      grouped[teacherId].schedules.push(schedule);
    });

    // Convert Map to Array
    Object.keys(grouped).forEach(teacherId => {
      grouped[teacherId].classes = Array.from(grouped[teacherId].classes.values());
    });

    return Object.values(grouped);
  }, [schedules, dateFilter, selectedDate]);

  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
    }
    return name.charAt(0).toUpperCase();
  };

  const getDateDisplay = () => {
    if (!selectedDate) return '';
    
    // Format as DD/MM/YYYY
    const day = String(selectedDate.getDate()).padStart(2, '0');
    const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
    const year = selectedDate.getFullYear();
    const dateStr = `${day}/${month}/${year}`;

    switch (dateFilter) {
      case 'yesterday':
        return `أمس (${dateStr})`;
      case 'today':
      case 'daily':
        return `اليوم (${dateStr})`;
      case 'tomorrow':
        return `غداً (${dateStr})`;
      case 'weekly':
        return `الأسبوع الحالي (${dateStr})`;
      case 'monthly':
        return `الشهر الحالي (${dateStr})`;
      default:
        return dateStr;
    }
  };

  if (teacherSchedules.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          لا توجد جداول للمعلمين
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          يرجى اختيار فلاتر مختلفة
        </Typography>
      </Paper>
    );
  }

  return (
    <Box>
      <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mb: 3 }}>
        جدول المعلمين حسب الصفوف - {getDateDisplay()}
      </Typography>

      <Grid container spacing={3}>
        {teacherSchedules.map((teacher) => {
          const totalHours = teacher.schedules.reduce((sum, s) => sum + (s.duration_minutes || 0), 0) / 60;

          return (
            <Grid item xs={12} key={teacher.teacherId}>
              <Card elevation={2} sx={{ borderRadius: 3 }}>
                <CardContent>
                  <Stack spacing={2}>
                    {/* Teacher Header */}
                    <Stack useFlexGap direction="row" spacing={2} alignItems="center">
                      <Avatar
                        sx={{
                          bgcolor: 'primary.main',
                          width: 56,
                          height: 56,
                          fontSize: '1.2rem',
                          fontWeight: 'bold'
                        }}
                      >
                        {getInitials(teacher.teacherName)}
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" fontWeight="bold">
                          {teacher.teacherName}
                        </Typography>
                        <Stack useFlexGap direction="row" spacing={2} sx={{ mt: 0.5 }}>
                          <Chip
                            size="small"
                            icon={<AccessTimeIcon />}
                            label={`${totalHours.toFixed(1)} ساعة`}
                            color="primary"
                            variant="outlined"
                          />
                          <Chip
                            size="small"
                            icon={<SchoolIcon />}
                            label={`${teacher.classes.length} صف`}
                            color="secondary"
                            variant="outlined"
                          />
                          <Chip
                            size="small"
                            icon={<BookIcon />}
                            label={`${teacher.schedules.length} حصة`}
                            color="info"
                            variant="outlined"
                          />
                        </Stack>
                      </Box>
                    </Stack>

                    <Divider />

                    {/* Classes Table */}
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow sx={{ bgcolor: 'grey.100' }}>
                            <TableCell sx={{ fontWeight: 'bold' }}>الصف</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>المادة</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>الوقت</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>المدة</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>القاعة</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>اليوم</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {teacher.classes.map((classItem) =>
                            classItem.schedules
                              .sort((a, b) => (a.start_time || '').localeCompare(b.start_time || ''))
                              .map((schedule, idx) => (
                                <TableRow key={`${classItem.classId}-${idx}`} hover>
                                  {idx === 0 && (
                                    <TableCell
                                      rowSpan={classItem.schedules.length}
                                      sx={{
                                        fontWeight: 'bold',
                                        bgcolor: 'primary.light',
                                        color: 'primary.contrastText',
                                        verticalAlign: 'middle'
                                      }}
                                    >
                                      {classItem.className}
                                    </TableCell>
                                  )}
                                  <TableCell>
                                    <Chip
                                      size="small"
                                      label={schedule.subject_name || 'غير محدد'}
                                      sx={{
                                        bgcolor: schedule.color ? `${schedule.color}20` : 'grey.200',
                                        color: schedule.color || 'text.primary'
                                      }}
                                    />
                                  </TableCell>
                                  <TableCell>
                                    {schedule.start_time} - {schedule.end_time}
                                  </TableCell>
                                  <TableCell>{schedule.duration_minutes || 0} دقيقة</TableCell>
                                  <TableCell>{schedule.classroom || '-'}</TableCell>
                                  <TableCell>
                                    {getDayName(schedule.day_of_week)}
                                  </TableCell>
                                </TableRow>
                              ))
                          )}
                          {teacher.classes.length === 0 && (
                            <TableRow>
                              <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                                <Typography variant="body2" color="text.secondary">
                                  لا توجد حصص مجدولة
                                </Typography>
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default EducationalScheduleTeachersView;
